"use client";

import { use } from "react";
import { Link } from "@/i18n/navigation";
import { formatDate } from "@/lib/utils";
import { ArrowLeft, Loader2 } from "@/components/icons";
import { Footer } from "@/components/footer";
import { Navbar } from "@/components/navbar";
import { useBlogPost } from "@/features/blog/api/queries";
import { BlogContent } from "@/features/blog/blog-content";

function fmtDate(s: string | null): string {
  if (!s) return "";
  return formatDate(s);
}

export default function BlogPostPage({ params }: { params: Promise<{ slug: string }> }) {
  const { slug } = use(params);

  const { data: post, isPending: loading, isError: notFound } = useBlogPost(slug);

  return (
    <div className="marketing-surfaces min-h-screen bg-surface-base text-text-primary flex flex-col">
      <Navbar />

      <main className="flex-1 px-5 sm:px-8 py-12 w-full">
        {loading ? (
          <div className="flex items-center justify-center py-40">
            <Loader2 className="size-6 animate-spin text-text-quaternary" />
          </div>
        ) : notFound || !post ? (
          <div className="py-40 text-center">
            <p className="text-lg text-text-tertiary">Post not found.</p>
            <Link href="/blog" className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-brand">
              <ArrowLeft className="size-4" />
              Back to blog
            </Link>
          </div>
        ) : (
          <article className="mx-auto max-w-3xl pt-8">
            {/* Back */}
            <Link
              href="/blog"
              className="inline-flex items-center gap-1.5 text-sm text-text-quaternary transition-colors hover:text-text-primary"
            >
              <ArrowLeft className="size-4" />
              Blog
            </Link>

            {/* Header */}
            <header className="mt-8">
              <div className="flex items-center gap-3 text-sm text-text-quaternary">
                <span className="font-medium text-brand">{post.tag}</span>
                <span>·</span>
                <time>{fmtDate(post.published_at)}</time>
              </div>
              <h1 className="mt-4 text-3xl sm:text-4xl md:text-[2.75rem] font-semibold tracking-tight leading-[1.15]">
                {post.title}
              </h1>
              {post.excerpt && (
                <p className="mt-5 text-lg leading-relaxed text-text-tertiary">{post.excerpt}</p>
              )}
              {post.author_name && (
                <div className="mt-7 flex items-center gap-3">
                  {post.author_avatar_url ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={post.author_avatar_url}
                      alt={post.author_name}
                      className="size-9 rounded-full object-cover"
                    />
                  ) : (
                    <span className="flex size-9 items-center justify-center rounded-full bg-brand/10 text-sm font-semibold text-brand">
                      {post.author_name
                        .split(/\s+/)
                        .slice(0, 2)
                        .map((w) => w[0])
                        .join("")
                        .toUpperCase()}
                    </span>
                  )}
                  <div>
                    <p className="text-sm font-medium text-text-primary">{post.author_name}</p>
                    <p className="text-xs text-text-quaternary">{post.author_title || "Encrata"}</p>
                  </div>
                </div>
              )}
            </header>

            {/* Cover */}
            {post.cover_image_url && (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={post.cover_image_url}
                alt=""
                className="mt-10 aspect-8/5 w-full rounded-2xl border border-border-secondary object-cover"
              />
            )}

            {/* Content */}
            <BlogContent
              content={post.content}
              className="
                mt-10 max-w-none text-base text-text-secondary leading-[1.8]
                [&>*:first-child]:mt-0
                [&_h1]:text-2xl [&_h2]:text-xl [&_h3]:text-lg
                [&_h1]:font-semibold [&_h2]:font-semibold [&_h3]:font-semibold
                [&_h1]:text-text-primary [&_h2]:text-text-primary [&_h3]:text-text-primary
                [&_h1]:tracking-tight [&_h2]:tracking-tight [&_h3]:tracking-tight
                [&_h1]:mt-12 [&_h2]:mt-12 [&_h3]:mt-10
                [&_h1]:mb-4 [&_h2]:mb-4 [&_h3]:mb-3
                [&_p]:my-5
                [&_ul]:my-5 [&_ul]:space-y-2.5 [&_ul]:pl-1
                [&_ol]:my-5 [&_ol]:space-y-2.5 [&_ol]:pl-5 [&_ol]:list-decimal
                [&_li]:relative [&_li]:pl-6
                [&_ul_li]:before:content-[''] [&_ul_li]:before:absolute [&_ul_li]:before:left-1
                [&_ul_li]:before:top-[0.7em] [&_ul_li]:before:size-1.5 [&_ul_li]:before:rounded-full
                [&_ul_li]:before:bg-brand
                [&_ol_li]:pl-2
                [&_strong]:text-text-primary [&_strong]:font-semibold
                [&_a]:text-brand [&_a]:underline [&_a]:decoration-brand/40 hover:[&_a]:decoration-brand
                [&_blockquote]:border-l-2 [&_blockquote]:border-border-secondary [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-text-tertiary
                [&_code]:bg-surface-raised [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-sm
                [&_pre]:my-6 [&_pre]:overflow-x-auto [&_pre]:rounded-xl [&_pre]:border [&_pre]:border-border-secondary [&_pre]:bg-surface-raised [&_pre]:p-4
                [&_img]:my-8 [&_img]:rounded-xl [&_img]:border [&_img]:border-border-secondary
                [&_hr]:my-10 [&_hr]:border-border-secondary
              "
            />

            {/* Footer CTA */}
            <div className="mt-16 border-t border-border-secondary/60 pt-10 pb-4">
              <p className="text-sm text-text-tertiary">
                Want to try Encrata?{" "}
                <Link href="/signup" className="font-medium text-brand hover:text-brand-hover">
                  Start with 500 free credits
                </Link>
                {" "}- no credit card needed.
              </p>
            </div>
          </article>
        )}
      </main>

      <Footer />
    </div>
  );
}
